import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'utils/axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  CircularProgress,
  Typography,
  Grid,
  Paper
} from '@mui/material';
import authService from 'services/AuthService';

const AddUser = () => {
  const { t } = useTranslation();
  const [userData, setUserData] = useState({
    username: '',
    email: '',
    roles: [],
    password: '',
    name: '',
    surname: '',
    departmentId: null
  });
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSelectChange = (e) => {
    setSelectedItem(e.target.value);
  };

  const handleDepartmentChange = (e) => {
    setSelectedDepartment(e.target.value);
    setUserData(prevState => ({ ...prevState, departmentId: e.target.value }));
  };

  const goBackToUsers = () => {
    navigate('/users');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      userData.roles = [{ RoleId: selectedItem }];
      const response = await axios.post('/users', userData);
      if (response.status === 200) {
        toast.success(t('user_added_successfully'));
        setUserData({
          username: '', email: '', password: '', name: '', surname: '', departmentId: null
        });
        setSelectedItem('');
        setSelectedDepartment('');
      } else {
        toast.error(t('failed_to_add_user'));
      }
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error(error.response?.data || t('failed_to_add_user'));
    }
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axios.get('users/role');
        setRoles(response.data.items);
      } catch {
        toast.error(t('failed_to_load_roles'));
      } finally {
        setLoading(false);
      }
    };

    const fetchDepartments = async () => {
      try {
        const response = await axios.get('codebook?codebook=departments');
        setDepartments(response.data);
      } catch {
        toast.error(t('failed_to_load_departments'));
      }
    };

    fetchRoles();
    fetchDepartments();
  }, [t]);

  if (loading) return <CircularProgress />;
  return (
    <Box maxWidth="md" mx="auto" mt={4}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{t('add_new_user')}</Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('username')}
                name="username"
                value={userData.username}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('password')}
                name="password"
                type="password"
                value={userData.password}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('name')}
                name="name"
                value={userData.name}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('surname')}
                name="surname"
                value={userData.surname}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('email')}
                name="email"
                value={userData.email}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>{t('role')}</InputLabel>
                <Select
                  value={selectedItem}
                  onChange={handleSelectChange}
                  label={t('role')}
                >
                  <MenuItem value="">{t('select_role')}</MenuItem>
                  {roles.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {(selectedItem === 2 || selectedItem === 3) && (
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>{t('department')}</InputLabel>
                  <Select
                    value={selectedDepartment}
                    onChange={handleDepartmentChange}
                    label={t('department')}
                  >
                    <MenuItem value="">{t('select_department')}</MenuItem>
                    {departments.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} mt={3}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button variant="outlined" onClick={goBackToUsers}>
                  {t('cancel')}
                </Button>
                <Button type="submit" variant="contained">
                  {t('save')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );

};

export default AddUser;
